import { useEffect, useRef, type ReactNode } from 'react';
import { RICH_TEXT_MAX_LENGTH, richTextToPlain, sanitizeRichText } from '@akarya/asp-client';

/** The toolbar's sizes, as execCommand('fontSize') levels (the sanitizer maps them to em). */
const SIZES = [
  { level: '2', label: 'Small' },
  { level: '3', label: 'Normal' },
  { level: '5', label: 'Large' },
  { level: '6', label: 'Huge' },
] as const;

/**
 * A small contentEditable editor for the text widget's `options.html`. It formats with
 * execCommand (no editor dependency) and emits only sanitized html plus its plain text,
 * which older players and the playlist summary show.
 */
export function RichTextEditor({
  html,
  color,
  background,
  onChange,
}: {
  html: string;
  color: string;
  background: string | undefined;
  onChange: (html: string, plain: string) => void;
}): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  /** What this editor last emitted; anything else (undo, another widget) replaces the content. */
  const emitted = useRef<string | null>(null);
  /** The selection inside the editor, kept while a toolbar control (e.g. the color picker) has focus. */
  const range = useRef<Range | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (el && html !== emitted.current) {
      el.innerHTML = sanitizeRichText(html);
      emitted.current = html;
    }
  }, [html]);

  const saveSelection = (): void => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && ref.current?.contains(sel.getRangeAt(0).commonAncestorContainer)) {
      range.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const emit = (): void => {
    const el = ref.current;
    if (!el) return;
    const clean = sanitizeRichText(el.innerHTML).slice(0, RICH_TEXT_MAX_LENGTH);
    emitted.current = clean;
    onChange(clean, richTextToPlain(clean));
  };

  const exec = (command: string, value?: string): void => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (range.current && sel) {
      sel.removeAllRanges();
      sel.addRange(range.current);
    }
    // <font>/align output, which the sanitizer turns into em sizes and checked styles.
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand(command, false, value);
    saveSelection();
    emit();
  };

  // mousedown, not click: a click would take the selection out of the editor first.
  const tool = (command: string, label: ReactNode, title: string, value?: string): ReactNode => (
    <button
      type="button"
      className="btn small ghost"
      title={title}
      aria-label={title}
      onMouseDown={(e) => {
        e.preventDefault();
        exec(command, value);
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="rich-text">
      <div className="rich-text-toolbar" role="toolbar" aria-label="Text formatting">
        {tool('bold', <b>B</b>, 'Bold')}
        {tool('italic', <i>I</i>, 'Italic')}
        {tool('underline', <u>U</u>, 'Underline')}
        {tool('strikeThrough', <s>S</s>, 'Strikethrough')}
        <select
          title="Size"
          aria-label="Size"
          value=""
          onMouseDown={saveSelection}
          onChange={(e) => e.target.value && exec('fontSize', e.target.value)}
        >
          <option value="">Size</option>
          {SIZES.map((s) => (
            <option key={s.level} value={s.level}>
              {s.label}
            </option>
          ))}
        </select>
        <input
          type="color"
          title="Color of the selected text"
          aria-label="Color of the selected text"
          defaultValue={color}
          onMouseDown={saveSelection}
          onChange={(e) => exec('foreColor', e.target.value)}
        />
        {tool('justifyLeft', '⇤', 'Align left')}
        {tool('justifyCenter', '↔', 'Align center')}
        {tool('justifyRight', '⇥', 'Align right')}
        {tool('insertUnorderedList', '•', 'Bulleted list')}
        {tool('removeFormat', '⌫', 'Clear formatting')}
      </div>
      <div
        ref={ref}
        className="rich-text-area"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Text"
        style={{ color, background: background ?? 'var(--surface-2)' }}
        onInput={emit}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onBlur={saveSelection}
      />
    </div>
  );
}
